import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { SidebarNavigationModalPrepaymentAccountItemBlockConfig } from '@front-platform/content-builder';
import { sidebarNavigationModalPrepaymentAccountItemBlockResolveConfig } from '@front-platform/content-builder';
import { switchMap, timer } from 'rxjs';
import { Component, computed, effect, inject, input } from '@angular/core';

import { ModalsService } from '@core/services/modals/modals.service';
import { SidebarNavigationModalConfig } from '@shared/modals/sidebar-navigation-modal/sidebar-navigation-modal.component';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { TagAtomComponent } from '@shared/atoms/tag-atom/tag-atom.component';
import { LinkDirective } from '@core/directives/link.directive';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { formatMarketplacePrice } from '@shared/utils/currency/format-marketplace-price.util';
import { PrepaymentSelectorService } from '@common/selectors/prepayment.selector';
import { PrepaymentStoreDispatcher } from '@common/dispatchers/prepayment.dispatcher';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { toObservable } from '@angular/core/rxjs-interop';
import { ShopPickerEntities } from '@core/models/shop.model';
export interface SidebarNavigationModalPrepaymentAccountItemBlockRuntimeConfig {
  'shop_entity'?: ShopPickerEntities;
}

export type SidebarNavigationModalPrepaymentAccountItemBlockConfigInput = Partial<
  SidebarNavigationModalPrepaymentAccountItemBlockConfig &
    SidebarNavigationModalPrepaymentAccountItemBlockRuntimeConfig
>;
export type SidebarNavigationModalPrepaymentAccountItemBlockResolvedConfig = Omit<
  SidebarNavigationModalPrepaymentAccountItemBlockConfig,
  keyof SidebarNavigationModalPrepaymentAccountItemBlockRuntimeConfig
> &
  Partial<SidebarNavigationModalPrepaymentAccountItemBlockRuntimeConfig>;
export interface SidebarNavigationModalPrepaymentAccountItemBlockContract extends ConfigComponentContract<
  SidebarNavigationModalPrepaymentAccountItemBlockConfigInput,
  SidebarNavigationModalPrepaymentAccountItemBlockResolvedConfig
> {}

@Component({
  selector: 'app-sidebar-navigation-modal-prepayment-account-item-block',
  templateUrl: './sidebar-navigation-modal-prepayment-account-item-block.component.html',
  standalone: true,
  imports: [TagAtomComponent, LinkDirective, TranslateModule, AsyncPipe],
  host: {
    class: 'block relative w-full py-3 pl-14.5 pr-4 text-md',
    '[class.hidden]': '!canDisplayPrepaymentAccount() || !isAuthenticated()',
  },
})
export class SidebarNavigationModalPrepaymentAccountItemBlockComponent implements SidebarNavigationModalPrepaymentAccountItemBlockContract {
  readonly config = input<SidebarNavigationModalPrepaymentAccountItemBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    SidebarNavigationModalPrepaymentAccountItemBlockConfig,
    SidebarNavigationModalPrepaymentAccountItemBlockRuntimeConfig
  >(sidebarNavigationModalPrepaymentAccountItemBlockResolveConfig, () => ({
    config: this.config(),
  }));
  private readonly modalsService: ModalsService<SidebarNavigationModalConfig> =
    inject(ModalsService);
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly prepaymentSelectorService = inject(PrepaymentSelectorService);
  private readonly prepaymentStoreDispatcher = inject(PrepaymentStoreDispatcher);
  private readonly authStoreSelector = inject(AuthStoreSelector);
  private readonly marketplaceStoreSelector = inject(MarketplaceStoreSelector);

  public readonly title = computed(() => this.resolvedConfig()['title'] as unknown as string);
  public readonly path = computed(() => this.resolvedConfig()['path'] as unknown as string);
  public readonly shopEntity = computed(
    () => this.resolvedConfig()['shop_entity'] as unknown as ShopPickerEntities,
  );

  private readonly shopId = computed(() => {
    const shop = this.shopEntity()?.[0];
    return shop ? shop.id : '';
  });

  constructor() {
    effect(() => {
      this.prepaymentStoreDispatcher.needToDisplayUserBalance(this.shopId());
    });
  }

  protected readonly canDisplayPrepaymentAccount = computed(() =>
    this.prepaymentSelectorService.selectUserHasABalanceAccount(this.shopId())(),
  );

  protected readonly pathToPrepaymentAccountPage = computed(() => {
    return this.path();
  });

  protected readonly prepaymentBalanceBeforeOrder = computed(() =>
    this.prepaymentSelectorService.selectBalanceBeforeOrderInCents(this.shopId())(),
  );

  private readonly shopId$ = toObservable(this.shopId);
  // It seems that their is desync between signal and selector, so we use observable here
  protected readonly isLoading$ = this.shopId$.pipe(
    switchMap(shopId => this.prepaymentSelectorService.selectUserBalanceLoading$(shopId)),
  );

  protected readonly isAuthenticated = this.authStoreSelector.selectUserIsLoaded;

  protected readonly marketplaceCurrency = computed(
    () => this.marketplaceStoreSelector.selectMarketplace().currency || 'EUR',
  );

  protected formatBalance(balanceInCents: number | null | undefined): string {
    if (balanceInCents == null) {
      return '-';
    }
    return formatMarketplacePrice(balanceInCents, this.marketplaceCurrency(), {
      amountInCents: true,
    });
  }

  close(): void {
    // Tiny delay to prevent page change "flash"
    timer(1).subscribe(() => {
      this.modalsService.close('sidebar-navigation-modal');
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(
  SidebarNavigationModalPrepaymentAccountItemBlockComponent,
  { 'keyAliases': {} },
);
